// Helper type for old/new diff
export interface IFieldDiff<T> {
  old: T;
  new: T;
}

// Service type with potential diff values
export type IServiceValue = number | IFieldDiff<number>;

export interface IChangeReview {
  _id?: string;
  requestType: "carWash" | "contactPerson" | "price";
  owner:
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        country_code?: string;
        relationshipManager?: {
          _id: string;
          firstName?: string;
          lastName?: string;
          username?: string;
        } | null;
      }
    | string;
  carWash?:
    | {
        _id: string;
        legalName?: string;
        location?: {
          street?: string;
        };
      }
    | string;
  status: "pending" | "approved" | "rejected";
  reviewComment?: string;
  rmEscalationSent?: boolean;
  changes: {
    // A creation request stores raw values; an EDIT to an existing car wash
    // stores {old, new} so the admin UI can render a before/after. Every
    // consumer must handle both — isFieldDiff() is the discriminator.
    images?: string[] | IFieldDiff<string[]>;
    legalName?: string | IFieldDiff<string>;
    // Free-text parking notes. Optional on the Partner form, and either a raw
    // value (creation) or an {old,new} pair (an edit to an existing car wash).
    parkingDescription?: string | IFieldDiff<string>;
    location?: {
      latitude?: string | number | IFieldDiff<string | number>;
      longitude?: string | number | IFieldDiff<string | number>;
      street?: string | IFieldDiff<string>;
      description?: string | IFieldDiff<string>;
    };

    // For contact person change (can be diff or full object)
    contact?: {
      fullName?: string | IFieldDiff<string>;
      country_code?: string | IFieldDiff<string>;
      phone?: string | IFieldDiff<string>;
    };

    // For price changes (nested diff structure)
    services?: {
      sedan?: {
        inner?: IServiceValue;
        outer?: IServiceValue;
        both?: IServiceValue;
      };
      cuv?: {
        inner?: IServiceValue;
        outer?: IServiceValue;
        both?: IServiceValue;
      };
      suv?: {
        inner?: IServiceValue;
        outer?: IServiceValue;
        both?: IServiceValue;
      };
      minivan?: {
        inner?: IServiceValue;
        outer?: IServiceValue;
        both?: IServiceValue;
      };
      van?: {
        inner?: IServiceValue;
        outer?: IServiceValue;
        both?: IServiceValue;
      };
    };
  };
  createdAt?: Date;
  updatedAt?: Date;
  reviewedBy?:
    | {
        _id: string;
        username?: string;
      }
    | string;
  reviewedAt?: Date;
}

// Type guard to check if a value is a diff
export const isFieldDiff = <T>(value: any): value is IFieldDiff<T> => {
  return (
    value &&
    typeof value === "object" &&
    "old" in value &&
    "new" in value &&
    !Array.isArray(value)
  );
};

export interface IGetChangeReviewsRes {
  status: string;
  results: number;
  data: IChangeReview[];
}
